import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, BookOpen } from 'lucide-react';
import EBookTable from '../components/ebooks/EBookTable';
import EBookForm from '../components/ebooks/EBookForm';
import { EBook } from '@brovicms/models';
import { ebookService } from '../services';
import toast from 'react-hot-toast';
import { useEffect } from 'react';
import { ServiceResult } from '@/types/admin';
import { EBOOK_CATEGORIES } from '../constants/categories';
const EBooks = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingEbook, setEditingEbook] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [loading, setLoading] = useState(false);

  const [ebooks, setEbooks] = useState<EBook[]>([]);
  useEffect(() => {
    setLoading(true);
    const fetchEbooks = async () => {
      try {
        // Fetch all ebooks by using a very large limit
        const result = await ebookService.getEBooks();
        if (result.success) {
          setEbooks(result.data);
        } else {
          toast.error(result?.error?.message || 'Failed to fetch ebooks');
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to fetch ebooks');
      } finally {
        setLoading(false);
      }
    };
    fetchEbooks();
  }, []);

  const handleCreate = () => {
    navigate('/ebooks/new');
  };

  const handleEdit = (ebook: any) => {
    navigate(`/ebooks/${ebook.id}/edit`);
  };

  const handleView = (ebook: any) => {
    navigate(`/ebooks/${ebook.id}/view`);
  };

  const handleDelete = (ebookId: string) => {
    ebookService.deleteEBook(ebookId).then((result: ServiceResult<void>) => {
      if (result.success) {
        setEbooks(ebooks.filter(ebook => ebook.id !== ebookId));
      } else {
        toast.error(result.error || 'Failed to delete ebook');
      }
    }).catch(err => {
      toast.error(err instanceof Error ? err.message : 'Failed to delete ebook');
    });
  };

  const handleFormSubmit = (data: any) => {
    if (editingEbook) {
      // Update existing ebook
      setEbooks(ebooks.map(ebook =>
        ebook.id === editingEbook.id
          ? { ...ebook, ...data }
          : ebook
      ));
    } else {
      // Create new ebook
      const newEbook = {
        id: Date.now().toString(),
        ...data,
        createdAt: new Date().toISOString(),
        coverImage: { url: '/api/placeholder/100/150' },
      };
      setEbooks([newEbook, ...ebooks]);
    }
    setShowForm(false);
    setEditingEbook(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEbook(null);
  };

  const filteredEbooks = ebooks.filter((ebook) => {
    const matchesSearch = !searchTerm || ((ebook.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || (ebook.author || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !filterCategory || ebook.category?.name === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {editingEbook ? 'Edit E-book' : 'Add New E-book'}
            </h1>
            <p className="text-gray-600 mt-1">
              {editingEbook ? 'Update the e-book information' : 'Create a new e-book entry'}
            </p>
          </div>
        </div>
        <EBookForm
          ebook={editingEbook}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          loading={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading text-primary-800">E-books</h1>
          <p className="text-gray-600 mt-1">
            Manage your e-book collection
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add E-book
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search e-books..."
                  className="input pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="md:w-48">
              <select
                className="input"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {EBOOK_CATEGORIES.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-apple">
                <BookOpen className="w-5 h-5 text-primary-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total E-books</p>
                <p className="text-lg font-semibold text-gray-900">{ebooks.length}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-success-100 rounded-apple">
                <BookOpen className="w-5 h-5 text-success-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-lg font-semibold text-gray-900">
                  {ebooks.filter(ebook => ebook.isPublished).length}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-warning-100 rounded-apple">
                <BookOpen className="w-5 h-5 text-warning-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-lg font-semibold text-gray-900">
                  {ebooks.filter(ebook => !ebook.isPublished).length}
                </p>
              </div>
            </div>
          </div>
        </div> */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-error-100 rounded-apple">
                <BookOpen className="w-5 h-5 text-error-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Premium</p>
                <p className="text-lg font-semibold text-gray-900">
                  {ebooks.filter(ebook => ebook.isPremium).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* E-books Table */}
      <EBookTable
        ebooks={filteredEbooks}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
      />
    </div>
  );
};

export default EBooks;
